import React, { FC, ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Grid, Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
} from '@material-ui/lab';
import { useFormikContext } from 'formik';
import { useDebounce } from 'use-debounce';
import CloseIcon from '@material-ui/icons/Close';

import { ProfileSuggestionFragment, UpdateGroupInput, useUserSuggestQuery } from 'api';
import { UserAvatar, LoaderComponents } from 'components';
import { useAutoCompleteStyles, PickerInput, PopupIcon } from 'components/Pickers';
import { useNotify } from 'hooks';
import { useGroupData } from 'features/groups/hooks';
import { getGroupHexColor } from 'features/groups/helpers';

type OnChange = [ChangeEvent, ProfileSuggestionFragment[], AutocompleteChangeReason];

interface IProps {
  isMutating: boolean;
  excludeIds: string[];
  ownerId: string;
  value: ProfileSuggestionFragment[];
  onChange(...onChange: OnChange): void;
}

const useStyles = makeStyles(() => ({
  chip: {
    borderRadius: 4,
    '&$chipDisabled': {
      opacity: 1,
    },
  },
  chipDisabled: {},
  deleteIcon: {
    lineHeight: 1.5,
    width: 'inherit',
  },
}));

const ModeratorPicker: FC<IProps> = ({ isMutating, excludeIds, ownerId, value, onChange }) => {
  const classes = useStyles();
  const autoCompleteClasses = useAutoCompleteStyles();
  const { t } = useTranslation();
  const notify = useNotify();
  const { groupColors } = useGroupData();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const { values } = useFormikContext<UpdateGroupInput>();
  const hexColor = getGroupHexColor(groupColors, values.color);

  const [prefix] = useDebounce(inputValue, 100);
  const { data, isLoading: isSuggestLoading } = useUserSuggestQuery(
    { prefix },
    {
      onError: notify.queryError,
      enabled: !!prefix,
    }
  );

  const isLoading = isSuggestLoading || isMutating;
  const options = data?.suggest?.results ?? [];
  const filteredOptions = options.filter(result => !excludeIds.includes(result.id));

  function toggleOpen() {
    setOpen(!open);
  }

  function handleChange(...args: OnChange) {
    setInputValue('');
    onChange(...args);
  }

  function handleInputChange(nextValue: string) {
    setInputValue(nextValue);
  }

  function renderTags(tagValue: ProfileSuggestionFragment[], getTagProps: AutocompleteGetTagProps) {
    return tagValue.map((profile, index: number) => {
      const isOwner = profile.id === ownerId;
      const deleteIcon = isOwner ? <Typography>{t('groups:roleOwner')}</Typography> : <CloseIcon />;
      const chipClasses = {
        root: classes.chip,
        deleteIcon: classes.deleteIcon,
        disabled: classes.chipDisabled,
      };

      return (
        <Chip
          key={profile.id}
          classes={chipClasses}
          avatar={<UserAvatar size={24} user={profile} />}
          label={profile.title || <LoaderComponents.Block width={80} height={16} />}
          deleteIcon={deleteIcon}
          {...getTagProps({ index })}
          disabled={isOwner}
        />
      );
    });
  }

  function renderOption(option: ProfileSuggestionFragment) {
    return (
      <Grid container alignItems="center">
        <Grid item>
          <Box mr={1}>
            <UserAvatar link size={30} user={option} />
          </Box>
        </Grid>
        <Grid item xs>
          {option.title}
        </Grid>
      </Grid>
    );
  }

  function renderInput(params: AutocompleteRenderInputParams) {
    return (
      <PickerInput
        working={isLoading}
        placeholder="groups:addManagers"
        onChange={handleInputChange}
        params={params}
      />
    );
  }

  return (
    <Autocomplete
      multiple
      fullWidth
      disableClearable
      disableCloseOnSelect
      classes={autoCompleteClasses}
      open={open}
      value={value}
      onOpen={toggleOpen}
      onClose={toggleOpen}
      inputValue={inputValue}
      loading={isLoading}
      onChange={handleChange}
      getOptionLabel={({ title }: ProfileSuggestionFragment) => title}
      popupIcon={<PopupIcon open={open} color={hexColor} />}
      renderTags={renderTags}
      renderInput={renderInput}
      renderOption={renderOption}
      options={filteredOptions}
    />
  );
};

export default ModeratorPicker;
